import { ChangeEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../../../../services/redux/hooks/use-selector";
import { selectAssistantValue } from "../../../../redux/selectors";
import { useUpdateStorageValue } from "../../../../../../app/state/storageObjectSlice/hook/use-update-storage-value";
import { setAssistantValue } from "../../../../redux/slice";
import { getUpdatedValue } from "../../../../helpers/getUpdatedValue";
import { TModalsIds } from "../../../../../../app/state/modalSlice/types";
import { TEntityInfo } from "../../../../../../common/types/utility";
import { AppSwitch } from "../../../../../../shared/components/AppSwitch";
import styles from "./styles.module.css";
import { getInstructionWithoutHiddenPartWithTrim } from "../../../../helpers/getInstructionWithoutHiddenPart";
import { rebuildSecurityBlocks } from "../../../../helpers/rebuildSecurityBlocks";

interface Props {
  openAttachAssistantsModal: (id: TModalsIds, info: TEntityInfo | null) => void;
}

export const AgentModeSwitcher = ({ openAttachAssistantsModal }: Props) => {
  const assistantValue = useAppSelector(selectAssistantValue);
  const { saveValue } = useUpdateStorageValue("assistants", setAssistantValue);
  const [assistantMode, setAssistantMode] = useState<"agent" | "assistant">(
    "assistant",
  );
  const { t } = useTranslation();

  useEffect(() => {
    if (!assistantValue?.meta?.agent_mode) {
      setAssistantMode("assistant");
      return;
    }
    assistantValue?.meta?.agent_mode
      ? setAssistantMode("agent")
      : setAssistantMode("assistant");
  }, [assistantValue?.meta?.agent_mode]);

  const handleChangeMode = (event: ChangeEvent<HTMLInputElement>) => {
    if (!assistantValue?.meta) return;
    const instruction = assistantValue?.assistant_object?.instructions;
    const newMode = event.target.checked ? "agent" : "assistant";
    setAssistantMode(newMode);
    const newInstruction =
      newMode === "assistant"
        ? getInstructionWithoutHiddenPartWithTrim(instruction)
        : rebuildSecurityBlocks(
            instruction,
            assistantValue?.meta?.tools?.assistants ?? null,
            assistantValue?.meta?.tools?.skills ?? null,
          );
    const newValue = getUpdatedValue(
      assistantValue,
      {
        instructions: newInstruction,
      },
      {
        ...assistantValue.meta,
        agent_mode: event.target.checked,
      },
    );
    saveValue(newValue);
  };

  return (
    <div className={styles["flex-container"]}>
      <div>
        {assistantMode === "agent"
          ? t("agents.mode_switcher_label_agent")
          : t("agents.mode_switcher_label_assistant")}
      </div>
      <AppSwitch
        checked={assistantMode === "agent"}
        onChange={handleChangeMode}
      />
    </div>
  );
};
